import { useState, useEffect } from 'react';
import { Truck, Plus, Phone, Search, Trash2, Edit, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { AuthGuard } from '@/features/auth/components/auth-guard';
import { supabase } from '@/lib/supabase';

// Extended Rider interface with status field needed by UI
interface RiderWithStatus {
  id: string;
  user_id: string | null;
  full_name: string;
  phone: string;
  whatsapp_number: string | null;
  branch_id: string | null;
  is_active: boolean;
  created_at: string;
  status?: string;
}

interface Branch {
  id: string;
  name: string;
}

interface FunctionResponse<T> {
  data?: T;
  error?: string;
  message?: string;
  success?: boolean;
}

async function invokeAdminFunction<T>(
  name: string,
  options: { method?: string; body?: unknown } = {},
): Promise<FunctionResponse<T>> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await supabase.functions.invoke<FunctionResponse<T>>(name, options);

  if (error) {
    throw error;
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  if (data?.success === false) {
    throw new Error(data.error || data.message || 'Request failed');
  }

  return data || {};
}

export default function RidersPage() {
  const [riders, setRiders] = useState<RiderWithStatus[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingRider, setEditingRider] = useState<RiderWithStatus | null>(null);
  const [editRiderData, setEditRiderData] = useState({
    full_name: '',
    phone: '',
    whatsapp_number: '',
    branch_id: '',
    pin: '',
  });
  const [newRider, setNewRider] = useState({
    name: '',
    phone: '',
    whatsapp_number: '',
    branch_id: '',
    pin: '',
  });

  useEffect(() => {
    loadRiders();
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      const result = await invokeAdminFunction<Branch[]>('admin-branches', { method: 'GET' });
      setBranches(result.data || []);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadRiders = async () => {
    try {
      const result = await invokeAdminFunction<RiderWithStatus[]>('admin-riders', { method: 'GET' });
      const data = (result.data || []).map((rider) => ({
        ...rider,
        status: rider.status || (rider.is_active ? 'available' : 'offline'),
      }));
      setRiders(data);
    } catch (error) {
      console.error('Failed to load riders:', error);
      setRiders([
        { id: 'rider-001', full_name: 'John Doe', phone: '+254712345678', whatsapp_number: '+254712345678', branch_id: null, status: 'AVAILABLE', is_active: true, user_id: null, created_at: new Date().toISOString() },
        { id: 'rider-002', full_name: 'Jane Smith', phone: '+254723456789', whatsapp_number: '+254723456789', branch_id: null, status: 'BUSY', is_active: true, user_id: null, created_at: new Date().toISOString() },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRider = async () => {
    try {
      const result = await invokeAdminFunction<RiderWithStatus & { pin?: string }>('admin-riders', {
        method: 'POST',
        body: {
          full_name: newRider.name,
          phone: newRider.phone,
          whatsapp_number: newRider.whatsapp_number,
          branch_id: newRider.branch_id || null,
          pin: newRider.pin || undefined,
        },
      });

      setIsAddDialogOpen(false);
      const pin = result.data?.pin || newRider.pin || 'generated';
      setNewRider({ name: '', phone: '', whatsapp_number: '', branch_id: '', pin: '' });
      alert(`Rider added successfully! PIN: ${pin} (Share this with the rider)`);
      loadRiders();
    } catch (error) {
      console.error('Failed to add rider:', error);
      alert('Failed to add rider. Please try again.');
    }
  };

  const handleToggleStatus = async (rider: RiderWithStatus) => {
    try {
      await invokeAdminFunction<RiderWithStatus>('admin-riders', {
        method: 'PUT',
        body: { id: rider.id, is_active: !rider.is_active },
      });
      loadRiders();
    } catch (error) {
      console.error('Failed to toggle rider status:', error);
    }
  };

  const handleEditClick = (rider: RiderWithStatus) => {
    setEditingRider(rider);
    setEditRiderData({
      full_name: rider.full_name,
      phone: rider.phone,
      whatsapp_number: rider.whatsapp_number || '',
      branch_id: rider.branch_id || '',
      pin: '', // Don't show existing PIN for security
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateRider = async () => {
    if (!editingRider) return;

    try {
      await invokeAdminFunction<RiderWithStatus>('admin-riders', {
        method: 'PUT',
        body: {
          id: editingRider.id,
          full_name: editRiderData.full_name,
          phone: editRiderData.phone,
          whatsapp_number: editRiderData.whatsapp_number || null,
          branch_id: editRiderData.branch_id || null,
          pin: editRiderData.pin || undefined,
        },
      });

      setIsEditDialogOpen(false);
      setEditingRider(null);
      if (editRiderData.pin) {
        alert(`Rider updated successfully! New PIN: ${editRiderData.pin}`);
      }
      loadRiders();
    } catch (error) {
      console.error('Failed to update rider:', error);
      alert('Failed to update rider. Please try again.');
    }
  };

  const handleDeleteRider = async (riderId: string) => {
    if (!confirm('Are you sure you want to delete this rider?')) return;

    try {
      await invokeAdminFunction<null>(`admin-riders?id=${encodeURIComponent(riderId)}`, {
        method: 'DELETE',
      });
      loadRiders();
    } catch (error) {
      console.error('Failed to delete rider:', error);
    }
  };

  const filteredRiders = riders.filter((rider) =>
    rider.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rider.phone.includes(searchQuery)
  );

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'available':
      case 'active':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Available</Badge>;
      case 'busy':
      case 'on_delivery':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Busy</Badge>;
      case 'off_duty':
      case 'offline':
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800">Offline</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <AuthGuard requiredRole={['admin', 'super-admin']}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard requiredRole={['admin', 'super-admin']}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <Truck className="h-6 w-6 text-brand-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Rider Management</h1>
                  <p className="text-sm text-gray-500">Manage delivery riders and assignments</p>
                </div>
              </div>
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Rider
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search riders by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Riders Grid */}
          {filteredRiders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Truck className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No riders found</h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery ? 'Try a different search term' : 'Add your first rider to get started'}
                </p>
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rider
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRiders.map((rider) => (
                <Card key={rider.id} className={!rider.is_active ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{rider.full_name}</CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" />
                          {rider.phone}
                        </CardDescription>
                      </div>
                      {getStatusBadge(rider.status || 'OFFLINE')}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant={rider.is_active ? 'danger' : 'primary'}
                          size="sm"
                          onClick={() => handleToggleStatus(rider)}
                        >
                          {rider.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditClick(rider)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteRider(rider.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>

        {/* Add Rider Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Rider</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="rider-name">Name</Label>
                <Input
                  id="rider-name"
                  value={newRider.name}
                  onChange={(e) => setNewRider({ ...newRider, name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rider-phone">Phone Number</Label>
                <Input
                  id="rider-phone"
                  value={newRider.phone}
                  onChange={(e) => setNewRider({ ...newRider, phone: e.target.value })}
                  placeholder="+254712345678"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rider-whatsapp">WhatsApp Number (Optional)</Label>
                <Input
                  id="rider-whatsapp"
                  value={newRider.whatsapp_number}
                  onChange={(e) => setNewRider({ ...newRider, whatsapp_number: e.target.value })}
                  placeholder="254712345678"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rider-branch">Branch (Optional)</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select
                    id="rider-branch"
                    value={newRider.branch_id}
                    onChange={(e) => setNewRider({ ...newRider, branch_id: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border rounded-md"
                  >
                    <option value="">No branch assigned</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rider-pin">PIN (Optional)</Label>
                <Input
                  id="rider-pin"
                  type="password"
                  maxLength={4}
                  value={newRider.pin}
                  onChange={(e) => setNewRider({ ...newRider, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  placeholder="4 digits (auto-generated if blank)"
                />
                <p className="text-xs text-gray-500">Leave blank to auto-generate a random PIN</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddRider} disabled={!newRider.name || !newRider.phone}>
                Add Rider
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Rider Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Rider</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-rider-name">Name</Label>
                <Input
                  id="edit-rider-name"
                  value={editRiderData.full_name}
                  onChange={(e) => setEditRiderData({ ...editRiderData, full_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-rider-phone">Phone Number</Label>
                <Input
                  id="edit-rider-phone"
                  value={editRiderData.phone}
                  onChange={(e) => setEditRiderData({ ...editRiderData, phone: e.target.value })}
                  placeholder="+254712345678"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-rider-whatsapp">WhatsApp Number (Optional)</Label>
                <Input
                  id="edit-rider-whatsapp"
                  value={editRiderData.whatsapp_number}
                  onChange={(e) => setEditRiderData({ ...editRiderData, whatsapp_number: e.target.value })}
                  placeholder="254712345678"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-rider-branch">Branch (Optional)</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select
                    id="edit-rider-branch"
                    value={editRiderData.branch_id}
                    onChange={(e) => setEditRiderData({ ...editRiderData, branch_id: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border rounded-md"
                  >
                    <option value="">No branch assigned</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-rider-pin">Set/Change PIN</Label>
                <Input
                  id="edit-rider-pin"
                  type="password"
                  maxLength={4}
                  value={editRiderData.pin}
                  onChange={(e) => setEditRiderData({ ...editRiderData, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  placeholder="Enter 4-digit PIN to set/update"
                />
                <p className="text-xs text-gray-500">Leave blank to keep existing PIN</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRider} disabled={!editRiderData.full_name || !editRiderData.phone}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AuthGuard>
  );
}
